import { useEffect, useRef } from "react";
import {
  idleDelay,
  isStandardBootstrapFolder,
  sortFoldersForBootstrap,
  syncFolderFromServerCache,
  shouldSkipFolderWarm,
} from "@/lib/mail/folderWarmup";
import { enqueueStandardFolderWarm } from "@/lib/mail/folderHeadersPrefetch";
import { INBOX_BOOTSTRAP_FOLDER_GAP_MS } from "@/lib/constants/inbox";
import type { MailFolder } from "@/lib/types/mail";

type Options = {
  /** Attendre la liste dossiers complète (pas le fallback INBOX seul). */
  foldersReady?: boolean;
};

/**
 * Au 1er lancement : dossiers principaux = fenêtre 50 (DD02, IMAP seulement si vide).
 * Les autres dossiers restent en lecture DD02 (MAIL_PIPELINE R26).
 */
export function useMailBootstrap(
  accountId: number | undefined,
  folders: MailFolder[],
  options?: Options
): void {
  const foldersReady = options?.foldersReady ?? folders.length > 1;
  const foldersRef = useRef(folders);
  foldersRef.current = folders;

  const runGenRef = useRef(0);
  const bootstrappedAccountsRef = useRef(new Set<number>());

  useEffect(() => {
    if (!accountId || !foldersReady || folders.length === 0) return;

    if (bootstrappedAccountsRef.current.has(accountId)) {
      return;
    }

    const gen = ++runGenRef.current;
    const sorted = sortFoldersForBootstrap(foldersRef.current);
    const toWarm = sorted.filter((f) => !shouldSkipFolderWarm(accountId, f.name));
    const standard = toWarm.filter((f) => isStandardBootstrapFolder(f.name));
    const secondary = toWarm.filter((f) => !isStandardBootstrapFolder(f.name));

    if (standard.length > 0) {
      enqueueStandardFolderWarm(accountId, standard);
    }

    if (toWarm.length === 0 || secondary.length === 0) {
      bootstrappedAccountsRef.current.add(accountId);
      return;
    }

    let cancelled = false;

    void (async () => {
      for (const folder of secondary) {
        if (cancelled || gen !== runGenRef.current) break;
        await syncFolderFromServerCache(accountId, folder.name);
        await idleDelay(INBOX_BOOTSTRAP_FOLDER_GAP_MS);
      }

      if (!cancelled && gen === runGenRef.current) {
        bootstrappedAccountsRef.current.add(accountId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, folders.length, foldersReady]);
}
